import { FormControl, FormHelperText, InputLabel, OutlinedInput } from "@mui/material";
import { forwardRef, useCallback } from "react";
import { IMaskInput } from "react-imask";

/**
 * NumericMaskedInputBase
 */

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
  mask: string;
}

const NumericMaskedInputBase = forwardRef<HTMLInputElement, CustomProps>((props, refs) => {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      inputRef={refs}
      onAccept={(value: any, input: { unmaskedValue: string }) => {
        if (input.unmaskedValue.length > 0) {
          onChange({ target: { name: props.name, value } });
        } else {
          onChange({ target: { name: props.name, value: '' } });
        }
      }}
      lazy={false}
      overwrite={true}
      unmask={true}
    />
  );
});

/**
 * NumericMaskedInput
 */

type Props = {
  name: string,
  label: string,
  value: number | null,
  error?: boolean,
  helperText?: string,
  readOnly?: boolean,
  onChangeValue: (value: number | null) => void,
};

export const NumericMaskedInput = ({
  name,
  label,
  value,
  error,
  helperText,
  readOnly = false,
  onChangeValue,
}: Props) => {

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === undefined || event.target.value === null || event.target.value.length === 0) {
      onChangeValue(null);
    } else {
      try {
        const numberValue = Number(event.target.value);
        if (!isNaN(numberValue)) onChangeValue(numberValue / 100);
      } catch (e: unknown) {
        onChangeValue(null);
      }
    }
  }, [
    onChangeValue,
  ]);

  return (
    <FormControl variant="outlined" size="small" fullWidth={true}>
      <InputLabel htmlFor={`input-${name}`} shrink={true} size="small" error={error}>{label}</InputLabel>
      <OutlinedInput
        size="small"
        id={`input-${name}`}
        name={name}
        value={value !== null ? (Math.round((value + Number.EPSILON) * 100)).toString() : ''}
        label={label}
        notched={true}
        readOnly={readOnly}
        error={error}
        fullWidth={true}
        aria-describedby={`${name}-component-error-text`}
        onChange={handleChange}
        inputComponent={NumericMaskedInputBase as any}
        inputProps={{
          mask: [
            { mask: 'R$ \\0,\\00' },
            { mask: 'R$ \\0,00' },
            { mask: 'R$ 0,00' },
            { mask: 'R$ 00,00' },
            { mask: 'R$ 000,00' },
            { mask: 'R$ 0.000,00' },
            { mask: 'R$ 00.000,00' },
            { mask: 'R$ 000.000,00' },
            { mask: 'R$ 0.000.000,00' },
            { mask: 'R$ 00.000.000,00' },
            { mask: 'R$ 000.000.000,00' },
          ],
          placeholder: "R$ ___.___.___,__",
        }} />
      <FormHelperText id={`${name}-component-error-text`} error={true}>
        {helperText}
      </FormHelperText>
    </FormControl>
  );
}